import { createSelector } from 'reselect';
import { List, Map } from 'immutable';

import { getChildBirthday, getOrderToken, getOrderUser } from 'redux/selectors/orders';
import { getSpreadInfo, getYearOffset } from 'redux/selectors/books';
import { getActiveOrderId, getActiveSpreadId } from 'redux/selectors/app';
import { exifDateInRange } from 'helpers/time';
import { uploadUrl } from 'helpers/api';
import toDate from 'date-fns/toDate';

export const getPhotos = state => {
  return state.getIn(['photos', getActiveOrderId(state)], List());
};

export const getPicked = state => {
  return state.getIn(['orders', getActiveOrderId(state), 'picked'], Map());
};

export const getUploads = state => {
  return state.getIn(['orders', getActiveOrderId(state), 'uploads'], Map());
};

export const getFavorites = state => {
  return state.getIn(['orders', getActiveOrderId(state), 'favorites', getActiveSpreadId(state)], List());
};

export const getSaved = state => {
  return state.getIn(['orders', getActiveOrderId(state), 'saved'], Map());
};

export const getSelection = createSelector(
  [getSpreadInfo, getChildBirthday, getYearOffset],
  (spreadInfo, childBirthdayString, yearOffset) => {
    let rangeStart = spreadInfo.get('filter_range_start', 0);
    let rangeEnd = spreadInfo.get('filter_range_end', 0) + 86400; // End of the day
    const childBirthday = childBirthdayString ? toDate(childBirthdayString) : null;

    if (spreadInfo.get('isMonthSpread')) {
      const index = spreadInfo.get('monthIndex');
      const d = new Date(childBirthday);
      d.setFullYear(d.getFullYear() + yearOffset);
      d.setMonth(childBirthday.getMonth() + index);
      d.setDate(1);

      rangeStart = (new Date(d).getTime() - childBirthday.getTime()) / 1000;

      const d2 = new Date(childBirthday);
      d2.setFullYear(d2.getFullYear() + yearOffset);
      d2.setMonth(childBirthday.getMonth() + index + 1);
      d2.setDate(0);

      rangeEnd = (new Date(d2).getTime() - childBirthday.getTime()) / 1000;
    }

    return {
      childBirthday,
      rangeStart,
      rangeEnd,
    };
  }
);

export const getFilterParams = filterType => {
  return createSelector([getActiveOrderId, getSaved, getFavorites], (activeOrderId, saved, favorites) => {
    if (filterType === 'saved') {
      return saved;
    } else if (filterType === 'favorites') {
      return favorites;
    }

    return null;
  });
};

export const getFilteredForPage = filterType => {
  return createSelector(
    [
      getPhotos,
      getFilterParams(filterType),
      getUploads,
      getPicked,
      getSelection,
      getActiveOrderId,
      getOrderToken,
      getOrderUser,
    ],
    (photos, filterParam, uploads, picked, selection, orderId, orderToken, orderUser) => {
      let filter = null;

      switch (filterType) {
        case 'saved':
          filter = photo => filterParam.indexOf(photo.get('simpleId')) > -1;
          break;
        case 'favorites':
          filter = photo => filterParam.indexOf(photo.get('simpleId')) > -1;
          break;
        default:
          break;
      }

      // Picked photos by simpleId
      const pickedById = picked.reduce((carry, value) => {
        if (value.get('simpleId')) {
          if (!(value.get('simpleId') in carry)) {
            carry[value.get('simpleId')] = 0;
          }

          carry[value.get('simpleId')]++;
        }

        return carry;
      }, {});

      if (filter) {
        photos = photos.filter(filter);
      }

      return photos
        .map(photo => {
          const uploadUID = uploads.getIn([photo.get('simpleId'), 'uid'], null);
          const photoDate = photo.get('exif', {}).DateTime;
          const { childBirthday, rangeStart, rangeEnd } = selection;

          const isSelection =
            photoDate && childBirthday ? exifDateInRange(childBirthday, photoDate, rangeStart, rangeEnd) : false;

          return photo.merge({
            uid: uploadUID,
            isSelection,
            useCount: pickedById[photo.get('simpleId')] || 0,
            uploadUrl: uploadUID ? uploadUrl(orderId, uploadUID, orderToken, orderUser) : null,
          });
        })
        .sortBy(
          photo =>
            photo.get('exif', {}).DateTime || photo.get('exif', {}).lastModified || '9999-' + photo.get('simpleId')
        )
        .sortBy(photo => !photo.get('isSelection'));
    }
  );
};

export const getPickedMoments = createSelector(
  [getPicked, getUploads, getPhotos, getActiveOrderId, getOrderToken, getOrderUser],
  (picked, uploads, photos, orderId, orderToken, orderUser) => {
    return picked.map(pick => {
      const simpleId = pick.get('simpleId');
      const uploadUID = uploads.getIn([simpleId, 'uid'], null);
      const photo = photos.find(photo => photo.get('simpleId') === simpleId, null);

      return pick.set(
        'photo',
        photo
          ? photo.merge({
              uid: uploadUID,
              uploadUrl: uploadUID ? uploadUrl(orderId, uploadUID, orderToken, orderUser) : null,
            })
          : null
      );
    });
  }
);



// WEBPACK FOOTER //
// ./app/redux/selectors/photos.js